
import React from 'react';
import { Control } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { TimeSlotFormData } from './timeSlotFormSchema';

interface RecurringSettingsFieldsProps {
  control: Control<TimeSlotFormData>;
}

const RecurringSettingsFields: React.FC<RecurringSettingsFieldsProps> = ({
  control,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
      <h4 className="text-sm font-medium">{t('schedule.form.recurringSettings')}</h4>
      
      {/* Time Range */}
      <div className="grid sm:grid-cols-2 gap-4">
        <FormField
          control={control}
          name="recurringStartTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('schedule.form.recurringStartTime')}</FormLabel>
              <FormControl>
                <Input type="time" {...field} className="text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="recurringEndTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('schedule.form.recurringEndTime')}</FormLabel>
              <FormControl>
                <Input type="time" {...field} className="text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Interval and Duration */}
      <div className="grid sm:grid-cols-2 gap-4">
        <FormField
          control={control}
          name="recurringInterval"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('schedule.form.recurringInterval')}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="text-base">
                    <SelectValue placeholder={t('schedule.form.selectInterval')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="15">{t('schedule.form.interval15')}</SelectItem>
                  <SelectItem value="30">{t('schedule.form.interval30')}</SelectItem>
                  <SelectItem value="45">{t('schedule.form.interval45')}</SelectItem>
                  <SelectItem value="60">{t('schedule.form.interval60')}</SelectItem>
                  <SelectItem value="90">{t('schedule.form.interval90')}</SelectItem>
                  <SelectItem value="120">{t('schedule.form.interval120')}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="slotDuration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('schedule.form.slotDuration')}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="text-base">
                    <SelectValue placeholder={t('schedule.form.selectDuration')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="15">{t('schedule.form.duration15')}</SelectItem>
                  <SelectItem value="30">{t('schedule.form.duration30')}</SelectItem>
                  <SelectItem value="45">{t('schedule.form.duration45')}</SelectItem>
                  <SelectItem value="60">{t('schedule.form.duration60')}</SelectItem>
                  <SelectItem value="75">{t('schedule.form.duration75')}</SelectItem>
                  <SelectItem value="90">{t('schedule.form.duration90')}</SelectItem>
                  <SelectItem value="120">{t('schedule.form.duration120')}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormDescription className="text-xs">
        {t('schedule.form.recurringDescription')}
      </FormDescription>
    </div>
  );
};

export default RecurringSettingsFields;
